/**
 * LIVE account funds / margin (read-only). Backed by
 * GET /admin/broker-accounts/{id}/funds.
 *
 * Brokers without a balance endpoint return 501 (capability gap, not an outage)
 * — the card detects that and shows "not available" rather than an error. Null
 * fields = the broker did not report that figure (never a misleading 0).
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get } from './client';

export const fundsSchema = z.object({
  cash_available: z.number().nullable(),
  margin_used: z.number().nullable(),
  margin_available: z.number().nullable(),   // headline buying power
  net_balance: z.number().nullable(),
  collateral: z.number().nullable(),
  unrealized_pnl: z.number().nullable(),
  realized_pnl: z.number().nullable(),
  currency: z.string(),
  as_of_time: z.string(),
  raw: z.unknown().nullable().optional(),    // money container, PII-stripped (forensic)
  source: z.string(),
});
export type Funds = z.infer<typeof fundsSchema>;

export const getFunds = (
  brokerAccountId: string,
  signal?: AbortSignal,
): Promise<ApiResult<Funds>> =>
  get(
    `/admin/broker-accounts/${encodeURIComponent(brokerAccountId)}/funds`,
    fundsSchema,
    { signal, retry: { attempts: 1 } },
  ) as Promise<ApiResult<Funds>>;
